<template>
  <div class="sign-up-researcher tw:py-6 tw:px-5 tw:bg-white">
    <h1 class="sign-up-researcher__title">Create Account</h1>
    <p class="sign-up-researcher__subtitle">Enter your personal data to create your account</p>
    <form novalidate class="sign-up-researcher__form" @submit.prevent="handleForm">
      <SignupCredentialsForm
        :input-data="inputData"
        :errors="errors"
        :is-email-valid="isEmailValid"
        :is-loading="isLoading"
        :is-empty-input="isEmptyInput"
        :active-input-name="activeInputName"
        email-label="Email"
        submit-text="Sign Up"
        :form-error="errors.default"
        @field-input="handleInput"
        @field-change="handleInput"
        @clear-field="clearInputAndErrors"
        @toggle-password="toggleShowPassword"
        @set-active-input="activeInputName = $event"
      />
    </form>
    <AuthInlineLinkPrompt action-text="Sign In" />
    <AuthTermsNotice />
    <div v-if="isLoading" class="sign-up-researcher__spinner">
      <Spinner />
    </div>
  </div>
</template>

<script>
import {
  checkFormInputsForCompleteness,
  checkFormInputsForErrors,
  checkEmailValidation,
  checkPasswordLength,
  toggleShowPassword,
  validatePassword,
  validateEmail,
  hideErrors,
} from '@/services/functions';
import Spinner from '../../components/Spinner';
import AuthInlineLinkPrompt from '@/_core/shared/ui/AuthInlineLinkPrompt/AuthInlineLinkPrompt.vue';
import SignupCredentialsForm from '@/_core/shared/ui/SignupCredentialsForm/SignupCredentialsForm.vue';
import AuthTermsNotice from '@/_core/shared/ui/AuthTermsNotice/AuthTermsNotice.vue';
import { mapGetters } from 'vuex';
import { store } from '@/store';

export default {
  components: {
    Spinner,
    AuthInlineLinkPrompt,
    SignupCredentialsForm,
    AuthTermsNotice,
  },
  data() {
    return {
      inputData: {
        email: '',
        password: '',
        firstName: '',
        lastName: '',
      },
      isEmailValid: null,
      isLoading: false,
      isEmptyInput: '',
      activeInputName: null,
    };
  },
  computed: {
    ...mapGetters({
      errors: 'authentication/errors',
    }),
  },
  methods: {
    clearInputAndErrors(inputField) {
      if (inputField === 'email') {
        this.inputData.email = '';
        hideErrors(['emailError', 'emailValidationError']);
      } else if (inputField === 'firstName') {
        this.inputData.firstName = '';
        hideErrors(['firstNameError']);
      } else if (inputField === 'lastName') {
        this.inputData.lastName = '';
        hideErrors(['lastNameError']);
      }
      store.commit('authentication/clearErrors');
      document.getElementById(inputField).style.border = '1px solid #F0F0F0';
    },
    async handleForm() {
      store.commit('authentication/clearErrors');

      const errorIds = [
        'emailError',
        'emailValidationError',
        'firstNameError',
        'lastNameError',
        'passwordError',
        'passwordLengthError',
      ];
      hideErrors(errorIds);

      const newUserData = {
        email: this.inputData.email,
        password: this.inputData.password,
        first_name: this.inputData.firstName,
        last_name: this.inputData.lastName,
        type: 'hacker',
        accept_terms_of_use_and_privacy_policy: true,
      };

      checkFormInputsForErrors(this.inputData, 'field-input-error');

      if (this.inputData.password) {
        checkPasswordLength({ password: this.inputData.password }, 'field-input-length-error');
      }

      if (this.inputData.email) {
        checkEmailValidation({ email: this.inputData.email }, 'field-input-validation-error');
      }

      if (
        checkFormInputsForCompleteness(this.inputData) &&
        validateEmail(this.inputData.email) &&
        validatePassword(this.inputData.password)
      ) {
        await this.sendRequest(newUserData);
      }
    },
    async sendRequest(newUserData) {
      try {
        this.isLoading = true;
        await this.$auth.signUpResearcher(newUserData);
        this.isLoading = false;
        if (!Object.keys(this.errors).length) {
          await this.$router.push({
            name: 'ConfirmEmailLink',
            params: { email: this.inputData.email },
          });
        }
      } catch (error) {
        this.isLoading = false;
        console.log(error);
      }
    },
    checkKeyEvent(event) {
      this.activeInputName = event.target.id;
    },
    handleInput(e) {
      this.checkKeyEvent(e);
      this.isEmailValid = validateEmail(this.inputData.email);

      hideErrors(['passwordError', 'passwordLengthError', 'emailError', 'emailValidationError']);
      document.getElementById('email').style.border = '1px solid #F0F0F0';
      document.getElementById('password').style.border = '1px solid #F0F0F0';
      store.commit('authentication/clearErrors');
    },
    toggleShowPassword,
  },
};
</script>

<style lang="scss">
@import '../../assets/css/fonts';

.sign-up-researcher {
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;

  &__spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    @include Aspekta-font(700, 60px, normal, #242424);
    font-size: calc(31px + (60 - 31) * calc((100vw - 320px) / (1920 - 320)));
    letter-spacing: 0em;
    margin: 0;
  }

  &__subtitle {
    width: 100%;
    max-width: 395px;
    @include Aspekta-font(500, 14px, 150%, #706969);
    margin: 12px 0 30px;
    text-align: center;
  }

  &__form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 395px;
    margin-top: 4px;
    margin-bottom: 12px;
  }
}

@media (max-width: 768px) {
  .sign-up-researcher {
    padding: 0 20px;

    &__title {
      margin: 0;
    }
  }
}
</style>
